// ─────────────────────────────────────────────────────────────────────────────
// gesture_templates.ts — Glyph reference library for gesture_matcher.ts
//
// Each template is a roughly-traced sequence of (x, y) points capturing the
// shape of a glyph.  After normalization the absolute scale doesn't matter,
// so traces are written in any convenient coordinate range — by convention
// roughly in [-1, 1] × [-1, 1] with origin near the centroid.
//
// Adding a new glyph: trace a representative drawing, drop the points here,
// pick aspect range / threshold, and add it to ALL_TEMPLATES below.  No
// other code changes needed — the matcher picks it up automatically.  To
// register multiple references for the same glyph (handles drawing
// variation), just add multiple templates with the same `id`.
// ─────────────────────────────────────────────────────────────────────────────

import type { GestureTemplate } from './gesture_matcher';

// ── Wind ────────────────────────────────────────────────────────────────────
// Long horizontal sweep ending in a small clockwise hook on the right.
// Reflection allowed: wind blows either direction.  Rotation is fixed
// (vertical "wind" makes no sense as a sweep gesture).
const WIND_POINTS: ReadonlyArray<{ x: number; y: number }> = [
  // Left start, drifting right, very gentle dip
  { x: -1.00, y:  0.00 },
  { x: -0.85, y:  0.05 },
  { x: -0.70, y:  0.10 },
  { x: -0.55, y:  0.15 },
  { x: -0.40, y:  0.18 },
  { x: -0.25, y:  0.15 },
  // Lift on the way to the hook
  { x: -0.10, y:  0.05 },
  { x:  0.05, y: -0.05 },
  { x:  0.20, y: -0.18 },
  { x:  0.32, y: -0.28 },
  { x:  0.42, y: -0.34 },
  // Approaching hook entry
  { x:  0.52, y: -0.36 },
  { x:  0.62, y: -0.32 },
  { x:  0.70, y: -0.22 },
  // Terminal CW loop
  { x:  0.74, y: -0.10 },
  { x:  0.70, y:  0.00 },
  { x:  0.60, y:  0.04 },
  { x:  0.50, y:  0.00 },
  { x:  0.45, y: -0.10 },
  { x:  0.50, y: -0.20 },
  { x:  0.60, y: -0.22 },
];

export const WIND_FLY: GestureTemplate = {
  id: 'wind.fly',
  points: WIND_POINTS,
  // Aspect dropped — with rotation tolerance below, the user's natural tilt
  // changes the bbox aspect, so locking aspect rejects valid-but-tilted
  // drawings.  The shape match itself filters non-wind candidates.
  rotationInvariant: true,
  reflectionAllowed: true,   // wind can blow either direction
  // DTW threshold scale: mean per-step Euclidean distance after alignment.
  // 0 = perfect, ~0.5 = random.  0.20 covers natural variation on hook
  // size / tail straightness while still rejecting unrelated shapes.
  // Tune up if your winds aren't matching, down if random scribbles match.
  threshold: 0.20,
};

// ── Active library ──────────────────────────────────────────────────────────

export const ALL_TEMPLATES: ReadonlyArray<GestureTemplate> = [
  WIND_FLY,
  // Future glyphs (templated and ready, just add the points):
  //   TRIANGLE_UP, TRIANGLE_DOWN, INFINITY, CIRCLE, …
];
